import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import FullScreenModal from "ds/components/FullScreenModal";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";
import useErrorHandle from "hooks/useErrorHandle";
import useCanCreateScan from "shared/Scan/access/useCanCreateScan";

import { SCHEDULE_SCAN_WIZARD_STEPS_ORDER } from "./constants";
import { NewScanScheduleContext } from "./context";
import NewScanScheduleFooter from "./Footer";
import { getDefaultFormValues, mapFormValuesToScanInput } from "./helpers";
import useScan from "./hooks/useScan";
import useScanCreate from "./hooks/useScanCreate";
import useScanUpdate from "./hooks/useScanUpdate";
import ScheduleScanWizardStepper from "./Stepper";
import ScheduleScanWizardStepAttachProviders from "./Steps/AttachProviders";
import ScheduleScanWizardStepAttachWorkerPool from "./Steps/StepAttachWorkerPool";
import ScheduleScanWizardStepDefineScope from "./Steps/DefineScope";
import ScheduleScanWizardStepFrequency from "./Steps/StepFrequency";
import ScheduleScanWizardStepResourceFilters from "./Steps/StepResourceFilters";
import ScheduleScanWizardStepScanDetails from "./Steps/StepScanDetails";
import ScheduleScanWizardStepSummary from "./Steps/StepSummary";
import { NewScanScheduleFormFields, NewScanScheduleStep } from "./types";

const STEP_COMPONENTS: Record<NewScanScheduleStep, () => ReactElement> = {
  [NewScanScheduleStep.ScanDetails]: ScheduleScanWizardStepScanDetails,
  [NewScanScheduleStep.AttachProviders]: ScheduleScanWizardStepAttachProviders,
  [NewScanScheduleStep.AttachWorkerPool]: ScheduleScanWizardStepAttachWorkerPool,
  [NewScanScheduleStep.DefineScope]: ScheduleScanWizardStepDefineScope,
  [NewScanScheduleStep.ResourceFilters]: ScheduleScanWizardStepResourceFilters,
  [NewScanScheduleStep.SetFrequency]: ScheduleScanWizardStepFrequency,
  [NewScanScheduleStep.Summary]: ScheduleScanWizardStepSummary,
};

const NewScanSchedule = () => {
  const { scanId } = useParams<{ scanId?: string }>();
  const navigate = useNavigate();
  const canCreateScan = useCanCreateScan();
  const { scan, loading: scanLoading, error: scanError } = useScan(scanId);
  const isEditMode = !!scanId;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = SCHEDULE_SCAN_WIZARD_STEPS_ORDER[currentStepIndex];

  const form = useForm<NewScanScheduleFormFields>({
    defaultValues: getDefaultFormValues(scan),
    mode: "onChange",
  });

  useEffect(() => {
    if (scan) {
      form.reset(getDefaultFormValues(scan));
    }
  }, [scan, form]);

  const [createScan, { loading: isCreating }] = useScanCreate();
  const [updateScan, { loading: isUpdating }] = useScanUpdate();

  const goToStep = useCallback((step: NewScanScheduleStep) => {
    setCurrentStepIndex(SCHEDULE_SCAN_WIZARD_STEPS_ORDER.indexOf(step));
  }, []);

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((index) =>
      Math.min(index + 1, SCHEDULE_SCAN_WIZARD_STEPS_ORDER.length - 1)
    );
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex((index) => Math.max(index - 1, 0));
  }, []);

  const contextValue = useMemo(
    () => ({
      currentStep,
      currentStepIndex,
      isEditMode,
      scan,
      goToStep,
      goToNextStep,
      goToPreviousStep,
    }),
    [currentStep, currentStepIndex, isEditMode, scan, goToStep, goToNextStep, goToPreviousStep]
  );

  const handleClose = useCallback(async () => {
    if (form.formState.isDirty) {
      await showSimpleLeaveConfirmation({
        title: "Do you want to leave scan scheduling?",
        message: "Your changes will not be saved.",
      });
    }

    navigate(isEditMode && scan ? `/resources/scans/${scan.id}` : "/resources/scans");
  }, [form.formState.isDirty, isEditMode, scan, navigate]);

  const handleFinish = form.handleSubmit((formValues) => {
    const input = mapFormValuesToScanInput(formValues);

    if (isEditMode && scan) {
      updateScan({ id: scan.id, input }, (data) => {
        if (data?.scanUpdate) {
          navigate(`/resources/scans/${data.scanUpdate.id}`);
        }
      });

      return;
    }

    createScan({ input }, (data) => {
      if (data?.scanCreate) {
        navigate(`/resources/scans/${data.scanCreate.id}`);
      }
    });
  });

  const ErrorContent = useErrorHandle(scanError);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!canCreateScan.result) {
    return <NotFoundPage />;
  }

  if (isEditMode && scanLoading && !scan) {
    return <PageLoading />;
  }

  if (isEditMode && !scan) {
    return <NotFoundPage />;
  }

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <FormProvider {...form}>
      <NewScanScheduleContext.Provider value={contextValue}>
        <FullScreenModal title="Schedule scan" onClose={handleClose}>
          <Box direction="row" grow="1" fullWidth overflowHidden>
            <ScheduleScanWizardStepper />

            <Box direction="column" grow="1" fullWidth relative>
              <StepComponent />

              <NewScanScheduleFooter
                onFinish={handleFinish}
                isSubmitting={isCreating || isUpdating}
              />
            </Box>
          </Box>
        </FullScreenModal>
      </NewScanScheduleContext.Provider>
    </FormProvider>
  );
};

export default NewScanSchedule;
